import { ExternalDocument } from '@brenger/api-client';
import * as React from 'react';
import { Translate } from 'react-localize-redux-dep-updated';
import { useMutation } from '@tanstack/react-query';
import { InputMeta, InputWrapper, ModifiedInput } from '.';
import { Button, CheckMarkList, IconTrash } from '../..';
import { coreClient } from '../../../utils/request';

export interface UploadedDocument {
  document: ExternalDocument;
  name: string;
}

interface Labels {
  default: React.ReactNode;
  add: React.ReactNode;
  delete: React.ReactNode;
  change: React.ReactNode;
}

interface Props extends ModifiedInput {
  meta?: InputMeta;
  customLabels?: Partial<Labels>;
  value?: UploadedDocument[];
  onChange: (value: UploadedDocument[]) => void;
}

export const InputFileUploads: React.FC<Props> = ({ meta, customLabels, value, onChange, ...inputProps }) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const createDocuments = useMutation({
    mutationFn: uploadFiles,
    onSuccess: data => {
      onChange(inputProps.multiple ? [...(value || []), ...data] : data);
      const fileInput = fileInputRef.current;
      if (fileInput) fileInput.value = '';
    },
  });

  const labels: Labels = {
    default: <Translate id="upload_document.labels.default" />,
    add: <Translate id="upload_document.labels.add" />,
    delete: <Translate id="upload_document.labels.delete" />,
    change: <Translate id="upload_document.labels.change" />,
    ...customLabels,
  };

  const addLabel = value?.length ? (inputProps.multiple ? labels.add : labels.change) : labels.default;
  return (
    <>
      <CheckMarkList
        list={(value || []).map((d, index) => {
          return (
            <small className="flex" style={{ marginTop: '0.25em' }} key={index}>
              {d.name}
              <button
                type="button"
                className="text-primary cursor-pointer trigger"
                style={{ marginLeft: '1em', appearance: 'none', border: 'none', background: 'none' }}
                onClick={() => onChange((value || []).filter((_, i) => i !== index))}
              >
                <IconTrash /> {labels.delete}
              </button>
            </small>
          );
        })}
      />
      <InputWrapper {...meta} className="input--file-uploads">
        <input
          {...inputProps}
          onChange={event => {
            const input = event.target;
            let files = input.files;

            // Enforce PDF-only if accept is application/pdf
            if (files && inputProps.accept === 'application/pdf') {
              const dt = new DataTransfer();
              Array.from(files).forEach(file => {
                if (file.type === 'application/pdf' && file.name.toLowerCase().endsWith('.pdf')) dt.items.add(file);
              });
              // Overwrite original files with the filtered ones
              files = dt.files;
              // clear input when no valid files are found
              if (files.length === 0) input.value = '';
            }

            if (!files) return;

            createDocuments.reset();
            createDocuments.mutate(files);
          }}
          ref={fileInputRef}
          type="file"
          className="hidden"
        />
        <Button loading={createDocuments.isPending} type="button" onClick={() => fileInputRef?.current?.click()}>
          {addLabel}
        </Button>
      </InputWrapper>
    </>
  );
};

const uploadFiles = async (files: FileList): Promise<UploadedDocument[]> => {
  const uploadPromises = Array.from(files).map(async file => {
    const formData = new FormData();
    formData.append('file', file);

    const document = await coreClient.files.createDocument({ data: formData });
    return {
      document,
      name: file.name,
    };
  });

  const uploadedDocuments = await Promise.all(uploadPromises);
  return uploadedDocuments;
};
